import { useEffect, useState, type RefObject } from "react";

/**
 * Text-selection hook for in-doc ask (FEATURE_MANUAL_ASK). Ported from ablearn
 * (same cycle2.0 pattern) — self-contained, no deps. Fires only for selections
 * inside the given container (the reading pane), debounced, length-bounded.
 */
export interface SelectionInfo {
  text: string;
  rect: DOMRect;
  truncated: boolean;
}

interface Opts {
  minChars: number;
  maxChars: number;
  debounceMs: number;
  containerRef?: RefObject<Element | null>;
}

export function useTextSelection(
  opts: Opts = { minChars: 2, maxChars: 2000, debounceMs: 120 }
): SelectionInfo | null {
  const [info, setInfo] = useState<SelectionInfo | null>(null);
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    function read(): void {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setInfo((prev) => (prev === null ? prev : null));
        return;
      }
      // Scope: BOTH endpoints must live inside the reading container — a drag
      // that starts in the prose but ends in the nav/dock is not an in-doc ask.
      const ref = opts.containerRef?.current;
      if (ref) {
        const { anchorNode, focusNode } = sel;
        if ((anchorNode && !ref.contains(anchorNode)) || (focusNode && !ref.contains(focusNode))) {
          setInfo((prev) => (prev === null ? prev : null));
          return;
        }
      }
      const raw = sel.toString();
      if (raw.length < opts.minChars) {
        setInfo((prev) => (prev === null ? prev : null));
        return;
      }
      const truncated = raw.length > opts.maxChars;
      const text = truncated ? raw.slice(0, opts.maxChars) : raw;
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      setInfo((prev) =>
        prev && prev.text === text && prev.truncated === truncated
          ? prev
          : { text, rect, truncated }
      );
    }
    function onChange(): void {
      if (timer) clearTimeout(timer);
      if (opts.debounceMs <= 0) {
        read();
        return;
      }
      timer = setTimeout(read, opts.debounceMs);
    }
    document.addEventListener("selectionchange", onChange);
    // The reading pane scrolls inside .guides-main (window never scrolls), so a
    // scroll moves the selection's viewport rect without firing selectionchange.
    // Re-read on any scroll (capture) to keep a fixed-position popover glued (#07).
    document.addEventListener("scroll", onChange, true);
    return () => {
      document.removeEventListener("selectionchange", onChange);
      document.removeEventListener("scroll", onChange, true);
      if (timer) clearTimeout(timer);
    };
  }, [opts.minChars, opts.maxChars, opts.debounceMs, opts.containerRef]);
  return info;
}
